import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, FileText, RefreshCw, Trash2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCatalog() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [productCount, setProductCount] = useState(null);
  const [source, setSource] = useState(null);
  const inputRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProductCount(data.count);
      setSource(data.source);
    } catch {
      /* noop */
    }
  };

  useEffect(() => { refresh(); }, []);

  const upload = async () => {
    if (!token) return toast.error("Admin token required");
    if (!file) return toast.error("Choose a CSV file first");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/catalog/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      localStorage.setItem("admin_token", token);
      setResult(data);
      toast.success(`Imported ${data.imported} products`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : detail?.message || "Upload failed");
      if (typeof detail === "object" && detail?.errors) setResult({ errors: detail.errors, imported: 0, skipped: detail.errors.length });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!token) return toast.error("Admin token required");
    if (!window.confirm("Clear the uploaded catalog and revert to the demo set?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/catalog`, { headers: { "X-Admin-Token": token } });
      toast.success("Catalog cleared. Demo products active.");
      refresh();
      setResult(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] py-16">
      <div className="max-w-3xl mx-auto px-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[var(--brand-gold-dark)]">Admin</p>
            <h1 className="font-serif-display text-4xl mt-2">Catalog upload</h1>
          </div>
          <a href="/" className="text-sm underline text-[var(--brand-ink-soft)]">← Back to site</a>
        </div>

        <div className="mt-8 border hairline bg-white p-6">
          <div className="flex items-center gap-3 text-sm">
            <span className="w-2 h-2 rounded-full bg-[var(--brand-gold)]" />
            <span>
              Active catalog: <strong>{productCount ?? "…"}</strong> products
              {source && <span className="ml-2 text-[var(--brand-ink-soft)]">({source})</span>}
            </span>
            <button onClick={refresh} className="ml-auto text-xs underline text-[var(--brand-ink-soft)]" data-testid="refresh-btn">
              <RefreshCw className="w-3 h-3 inline mr-1" /> Refresh
            </button>
          </div>
        </div>

        <div className="mt-6 border hairline bg-white p-6 space-y-5">
          <div>
            <label className="block text-[11px] tracking-[0.25em] uppercase text-[var(--brand-ink-soft)] mb-2">
              Admin token
            </label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="X-Admin-Token"
              data-testid="admin-token-input"
              className="w-full border hairline bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--brand-gold)]"
            />
          </div>

          <div>
            <label className="block text-[11px] tracking-[0.25em] uppercase text-[var(--brand-ink-soft)] mb-2">
              Products CSV
            </label>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              data-testid="csv-dropzone"
              className="cursor-pointer border border-dashed hairline px-6 py-10 text-center hover:border-[var(--brand-gold)] transition"
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                data-testid="csv-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-[var(--brand-gold-dark)]" />
                  <span>{file.name}</span>
                  <span className="text-[var(--brand-ink-soft)]">({Math.round(file.size / 1024)} KB)</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-[var(--brand-ink-soft)] text-sm">
                  <UploadCloud className="w-6 h-6 text-[var(--brand-gold-dark)]" />
                  Drop a CSV here, or click to browse
                </div>
              )}
            </div>
            <a
              href="/eternl_products_template.csv"
              download
              className="inline-block mt-3 text-xs underline text-[var(--brand-ink-soft)]"
              data-testid="download-template"
            >
              Download CSV template
            </a>
          </div>

          <div className="flex gap-3">
            <button
              onClick={upload}
              disabled={busy || !file}
              data-testid="upload-btn"
              className="btn-gold px-6 py-3 text-xs tracking-[0.2em] uppercase disabled:opacity-50"
            >
              {busy ? "Uploading…" : "Upload catalog"}
            </button>
            <button
              onClick={reset}
              disabled={busy}
              data-testid="reset-btn"
              className="btn-outline-ink px-6 py-3 text-xs tracking-[0.2em] uppercase inline-flex items-center gap-2"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Reset to demo
            </button>
          </div>
        </div>

        {result && (
          <div className="mt-6 border hairline bg-white p-6" data-testid="upload-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[var(--brand-gold-dark)]" />
              <h2 className="font-serif-display text-xl">
                Imported {result.imported}{result.skipped ? ` · ${result.skipped} skipped` : ""}
              </h2>
            </div>
            {result.categories && (
              <div className="mt-3 flex flex-wrap gap-2">
                {result.categories.map((c) => (
                  <span key={c} className="text-[11px] tracking-widest uppercase border hairline px-2.5 py-1">
                    {c}
                  </span>
                ))}
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs uppercase tracking-widest text-[var(--brand-wine)]">Errors</p>
                <ul className="mt-2 text-xs text-[var(--brand-ink-soft)] space-y-1 max-h-48 overflow-auto">
                  {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              </div>
            )}
          </div>
        )}

        <details className="mt-6 text-sm text-[var(--brand-ink-soft)]">
          <summary className="cursor-pointer">CSV format reference</summary>
          <div className="mt-3 space-y-2">
            <p>Required columns: <code>id, name, category, price, image_url</code></p>
            <p>Optional: <code>tags, original_price, discount_percent, short_description, benefits, product_url</code></p>
            <p><code>category</code> must be one of: shampoo, conditioner, mask, serum, oil, scalp-treatment, heat-protection, growth-therapy, colour-care.</p>
            <p><code>tags</code>: comma‑separated keywords (e.g. <code>repair,damage,dry,frizz</code>).</p>
            <p><code>benefits</code>: pipe‑separated bullets (e.g. <code>Keratin|Sulphate-free|Shine</code>).</p>
            <p><code>product_url</code>: full URL to the OpenCart product page so "Shop on Eternl" sends visitors to your real cart.</p>
          </div>
        </details>
      </div>
    </div>
  );
}
